import { Listing } from "../../../../../@types/listings/listing";
import { Grid, MenuItem, Select, FormControl, InputLabel } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useSettings from "hooks/useSettings";
import { CheckedMenuItemLabel } from "components/__haim/Common/CheckedMenuItemLabel";
import { typeIsOneOfListingTypes } from "utils/listings/helper";
import type { ChangeEvent, CSSProperties } from "react";
import type { MenuProps } from "@material-ui/core";

type ListingDialogInputFloorsProps = Pick<Listing, "listingType" | "floor" | "floorOutOf"> & {
  loading: boolean;
  handleChange: ({ target: { name, value } }: ChangeEvent<HTMLInputElement>) => void;
};

export function ListingDialogInputFloors({
  floor,
  loading,
  floorOutOf,
  listingType,
  handleChange
}: ListingDialogInputFloorsProps): JSX.Element {
  const { t: translate } = useTranslation();

  const { themeDirection } = useSettings();

  const handleGetLabel = (labelValue: number): string | number => {
    let translatedLabel: string | number = labelValue;

    if (labelValue === 0) translatedLabel = translate("listingsPage.ground") as string;

    return translatedLabel;
  };

  type FloorProps = Record<
    "floor" | "floorOutOf",
    {
      values: number | null;
      visible: boolean;
    }
  >;

  let floorProps: FloorProps = {
    floor: {
      values: floor,
      visible: true
    },
    floorOutOf: {
      values: floorOutOf,
      visible: typeof floor === "number"
    }
  };

  const showBothFloors = typeIsOneOfListingTypes(listingType, [
    "duplex",
    "offices",
    "apartment",
    "pentHouse",
    "officeBuilding",
    "gardenApartment",
    "storesCommercialSpace"
  ]);

  if (showBothFloors) {
    floorProps.floor.visible = true;
    floorProps.floorOutOf.visible = true;
  }

  const showBothFloorsWithValues = typeIsOneOfListingTypes(listingType, [
    "privateHouse",
    "semiDetached",
    "farm"
  ]);

  if (showBothFloorsWithValues) {
    floorProps.floor.visible = true;
    floorProps.floor.values = floor ?? 0;

    floorProps.floorOutOf.visible = true;
  }

  const showOnlyFloor = typeIsOneOfListingTypes(listingType, ["warehouses", "industrialBuildings"]);

  if (showOnlyFloor) {
    floorProps.floor.visible = true;

    floorProps.floorOutOf.visible = false;
    floorProps.floorOutOf.values = null;
  }

  const showNoFloors = typeIsOneOfListingTypes(listingType, ["lot"]);

  if (showNoFloors) {
    floorProps.floor.visible = false;
    floorProps.floor.values = null;

    floorProps.floorOutOf.visible = false;

    return null;
  }

  const floorLabel = translate("listingsPage.tableColumns.floor");
  const floorOutOfLabel = translate("listingsPage.tableColumns.floorOutOf");

  const isRTL = themeDirection === "rtl";

  const groundFloorOptionsStyle: CSSProperties = isRTL
    ? { direction: "ltr", textAlign: "right", justifyContent: "end" }
    : undefined;

  const parsedFloor = floorProps.floor.values;
  const parsedFloorOutOf = floorProps.floorOutOf.values;

  return (
    <>
      <Grid item xs={12} sm={6} md={4}>
        <FormControl fullWidth>
          <InputLabel>{floorLabel}</InputLabel>
          <Select
            name="floor"
            label={floorLabel}
            value={parsedFloor ?? ""}
            disabled={loading}
            MenuProps={selectStyle}
            onChange={handleChange}
            renderValue={handleGetLabel}
          >
            {emptyMenuItem}
            {groundFloorOptions.map((option) => (
              <MenuItem style={groundFloorOptionsStyle} value={option} key={option}>
                <CheckedMenuItemLabel selected={option === parsedFloor}>
                  {option}
                </CheckedMenuItemLabel>
              </MenuItem>
            ))}
            <MenuItem value={0}>
              <CheckedMenuItemLabel selected={parsedFloor === 0}>
                {translate("listingsPage.ground")}
              </CheckedMenuItemLabel>
            </MenuItem>
            {normalFloorOptions.map((option) => (
              <MenuItem value={option} key={option}>
                <CheckedMenuItemLabel selected={option === parsedFloor}>
                  {option}
                </CheckedMenuItemLabel>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {floorProps.floorOutOf.visible && (
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth>
            <InputLabel>{floorOutOfLabel}</InputLabel>
            <Select
              name="floorOutOf"
              label={floorOutOfLabel}
              value={parsedFloorOutOf ?? ""}
              disabled={loading}
              MenuProps={selectStyle}
              onChange={handleChange}
              renderValue={(selected) => selected}
            >
              {normalFloorOptions.map((option) => (
                <MenuItem value={option} key={option}>
                  <CheckedMenuItemLabel selected={option === parsedFloorOutOf}>
                    {option}
                  </CheckedMenuItemLabel>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
    </>
  );
}

export const emptyMenuItem = <MenuItem sx={{ height: 36 }} value={null} />;

const selectStyle: Partial<MenuProps> = {
  PaperProps: {
    sx: {
      maxHeight: 400
    }
  }
};

const normalFloorOptions = Array.from({ length: 50 }, (_, i) => i + 1);

const groundFloorOptions = Array.from({ length: 4 }, (_, i) => i + -4);
